import type { Theme } from "~/common/mui";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";

export const cartProductTableStyles = (theme: Theme) => ({
  ".scha__cart__product-table": {
    "&__header": {
      "& > *:nth-child(1)": { width: "16%" },
      "& > *:nth-child(2)": { width: "15%" },
      "& > *:nth-child(3)": { width: "14%" },
      "& > *:nth-child(4)": { width: "12%" },
      "& > *:nth-child(5)": { width: "12%" },
      "& > *:nth-child(6)": { width: "12%" },
      "& > *:nth-child(7)": { width: "12%" },
      "& > *:nth-child(8)": { width: "7%" },
    },
    "&__left-side": {
      width: "16%",
      paddingRight: "24px",
      position: "relative",
      "&__col-container": {
        display: "flex",
        "& > *:nth-child(1)": { width: "100%" },
      },
    },
    "&__right-side": {
      width: "84%",
      "&__col-container": {
        display: "flex",
        "& > *:nth-child(1)": { width: "17.8571%" },
        "& > *:nth-child(2)": { width: "16.6667%" },
        "& > *:nth-child(3)": { width: "14.2857%" },
        "& > *:nth-child(4)": { width: "14.2857%" },
        "& > *:nth-child(5)": { width: "14.2857%" },
        "& > *:nth-child(6)": { width: "14.2857%" },
        "& > *:nth-child(7)": { width: "8.3333%" },
      },
      "&__summary-container": {
        display: "flex",
        "& > *:nth-child(1)": { width: "48.8095%" },
        "& > *:nth-child(2)": { width: "42.8571%" },
        "& > *:nth-child(3)": { width: "8.3333%" },
      },
      "&__partial-deliveries-container": {
        display: "flex",
      },
    },
    [theme.breakpoints.up("desktop-m")]: {
      "&": {
        "&__header": {
          "& > *:nth-child(1)": { width: "14%" },
          "& > *:nth-child(2)": { width: "14%" },
          "& > *:nth-child(3)": { width: "14%" },
          "& > *:nth-child(4)": { width: "15%" },
          "& > *:nth-child(5)": { width: "12%" },
          "& > *:nth-child(6)": { width: "12%" },
          "& > *:nth-child(7)": { width: "12%" },
          "& > *:nth-child(8)": { width: "7%" },
        },
        "&__left-side": {
          width: "14%",
          paddingRight: "24px",
          position: "relative",
          "&__col-container": {
            display: "flex",
            "& > *:nth-child(1)": { width: "100%" },
          },
        },
        "&__right-side": {
          width: "86%",
          "&__col-container": {
            display: "flex",
            "& > *:nth-child(1)": { width: "16.2791%" },
            "& > *:nth-child(2)": { width: "16.2791%" },
            "& > *:nth-child(3)": { width: "17.4419%" },
            "& > *:nth-child(4)": { width: "13.9535%" },
            "& > *:nth-child(5)": { width: "13.9535%" },
            "& > *:nth-child(6)": { width: "13.9535%" },
            "& > *:nth-child(7)": { width: "8.1395%" },
          },
          "&__summary-container": {
            display: "flex",
            "& > *:nth-child(1)": { width: "50.5814%" },
            "& > *:nth-child(2)": { width: "41.8605%" },
            "& > *:nth-child(3)": { width: "8.1395%" },
          },
          "&__partial-deliveries-container": {
            display: "flex",
          },
        },
      },
    },
  },
  ".scha__cart__product-table__body-cell, .scha__cart__product-table__header-cell": {
    paddingRight: "8px",
    paddingLeft: "8px",
    "&.first": {
      paddingLeft: 0,
    },
    "&.first-on-right": {
      paddingLeft: "16px",
    },
    "&.last-on-right": {
      paddingRight: "16px",
    },
  },
  ".scha__cart__product-table__body-cell": {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  ".scha__cart__product-table__header-cell": {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    paddingTop: "20px",
    paddingBottom: "10px",
    "&.align-end": {
      alignItems: "flex-end",
    },
    "&__title": {
      position: "relative",
    },
    "&__subtitle": {
      position: "relative",
      color: theme.palette.text.secondary,
      fontWeight: "normal",
      marginTop: "5px",
    },
    "&__internal-bg": {
      background: theme.palette.surface.main,
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: "-50px",
    },
  },
  ".scha__cart__product-entry": {
    display: "flex",
    paddingTop: "16px",
    paddingBottom: "16px",
    "&--with-spacer:not(:last-child)": {
      borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
    },
  },
  ".scha__customer-special-price": {
    ...theme.typography.labelLarge,
    color: theme.palette.text.primary,
  },
});

const ACTIVE_QUOTE_TABLE_CLASS = "scha__active-quote__product-table";
const ACTIVE_QUOTE_TABLE_SELECTOR = `.${ACTIVE_QUOTE_TABLE_CLASS}`;
const ACTIVE_QUOTE_SHOW_DETAILS_SELECTOR = `.${ACTIVE_QUOTE_TABLE_CLASS}.${ACTIVE_QUOTE_TABLE_CLASS}--show-details`;

const toPercent = (fraction: number): string => `${Math.round(fraction * 100 * 10000) / 10000}%`;

const nthChildWidths = (values: number[], offset = 0, divisor = 1): Record<string, { width: string }> => {
  const rules: Record<string, { width: string }> = {};
  values.forEach((value, index) => {
    rules[`& > *:nth-child(${offset + index + 1})`] = { width: toPercent(value / divisor) };
  });
  return rules;
};

const rangeSum = (columns: number[], from: number, to: number): number =>
  columns.slice(from - 1, to).reduce((total, value) => total + value, 0);

interface TwoPartLayout {
  readonly leftTotal: number;
  readonly rightTotal: number;
  readonly leftColumns: number[];
  readonly rightColumns: number[];
  readonly header: Record<string, { width: string }>;
  readonly leftBody: Record<string, { width: string }>;
  readonly rightBody: Record<string, { width: string }>;
}

const buildTwoPartLayout = (leftTotal: number, leftColumns: number[], rightColumns: number[]): TwoPartLayout => {
  const rightTotal = 1 - leftTotal;
  return {
    leftTotal,
    rightTotal,
    leftColumns,
    rightColumns,
    header: {
      ...nthChildWidths(leftColumns, 0, 1),
      ...nthChildWidths(rightColumns, leftColumns.length, 1),
    },
    leftBody: nthChildWidths(leftColumns, 0, leftTotal),
    rightBody: nthChildWidths(rightColumns, 0, rightTotal),
  };
};

const NORMAL_LAYOUT = buildTwoPartLayout(0.14, [0.14], [0.115, 0.205, 0.19, 0.16, 0.105, 0.085]);
const SHOW_DETAILS_LAYOUT = buildTwoPartLayout(0.14, [0.14], [0.06, 0.12, 0.12, 0.1, 0.096, 0.09, 0.099, 0.09, 0.085]);

export const activeQuoteStyles = (theme: Theme) => ({
  [ACTIVE_QUOTE_TABLE_SELECTOR]: {
    "&__header": {
      display: "flex",
      border: "none !important",
      ...NORMAL_LAYOUT.header,
    },
    "&__left-side": {
      width: toPercent(NORMAL_LAYOUT.leftTotal),
      "&__col-container": {
        display: "flex",
        ...NORMAL_LAYOUT.leftBody,
      },
    },
    "&__right-side": {
      width: toPercent(NORMAL_LAYOUT.rightTotal),
      "&__col-container": {
        display: "flex",
        ...NORMAL_LAYOUT.rightBody,
      },
      "&__summary-container": {
        display: "flex",
        paddingTop: "20px",
        "& > *:nth-child(1)": {
          width: toPercent(rangeSum(NORMAL_LAYOUT.rightColumns, 1, 2) / NORMAL_LAYOUT.rightTotal),
        },
        "& > *:nth-child(2)": {
          width: toPercent(rangeSum(NORMAL_LAYOUT.rightColumns, 3, 4) / NORMAL_LAYOUT.rightTotal),
        },
        "& > *:nth-child(3)": {
          width: toPercent(rangeSum(NORMAL_LAYOUT.rightColumns, 5, 6) / NORMAL_LAYOUT.rightTotal),
        },
      },
    },
    "&__comment-input": {
      width: toPercent(
        (NORMAL_LAYOUT.rightTotal - NORMAL_LAYOUT.rightColumns[NORMAL_LAYOUT.rightColumns.length - 1]) /
          NORMAL_LAYOUT.rightTotal,
      ),
    },
  },

  [`${ACTIVE_QUOTE_SHOW_DETAILS_SELECTOR} .${ACTIVE_QUOTE_TABLE_CLASS}__header`]: {
    ...SHOW_DETAILS_LAYOUT.header,
  },
  [`${ACTIVE_QUOTE_SHOW_DETAILS_SELECTOR} .${ACTIVE_QUOTE_TABLE_CLASS}__right-side__col-container`]: {
    ...SHOW_DETAILS_LAYOUT.rightBody,
  },
  [`${ACTIVE_QUOTE_SHOW_DETAILS_SELECTOR} .${ACTIVE_QUOTE_TABLE_CLASS}__right-side__summary-container`]: {
    display: "flex",
    "& > *:nth-child(1)": {
      width: toPercent(rangeSum(SHOW_DETAILS_LAYOUT.rightColumns, 1, 2) / SHOW_DETAILS_LAYOUT.rightTotal),
    },
    "& > *:nth-child(2)": {
      width: toPercent(rangeSum(SHOW_DETAILS_LAYOUT.rightColumns, 3, 4) / SHOW_DETAILS_LAYOUT.rightTotal),
    },
    "& > *:nth-child(3)": {
      width: toPercent(SHOW_DETAILS_LAYOUT.rightColumns[4] / SHOW_DETAILS_LAYOUT.rightTotal),
    },
    "& > *:nth-child(4)": {
      width: toPercent(rangeSum(SHOW_DETAILS_LAYOUT.rightColumns, 6, 7) / SHOW_DETAILS_LAYOUT.rightTotal),
    },
    "& > *:nth-child(5)": {
      width: toPercent(SHOW_DETAILS_LAYOUT.rightColumns[7] / SHOW_DETAILS_LAYOUT.rightTotal),
    },
  },
  [`${ACTIVE_QUOTE_SHOW_DETAILS_SELECTOR} .${ACTIVE_QUOTE_TABLE_CLASS}__comment-input`]: {
    width: toPercent(rangeSum(SHOW_DETAILS_LAYOUT.rightColumns, 1, 5) / SHOW_DETAILS_LAYOUT.rightTotal),
  },
  [`${ACTIVE_QUOTE_SHOW_DETAILS_SELECTOR} .${ACTIVE_QUOTE_TABLE_CLASS}__internal-comment-input`]: {
    position: "relative",
    width: toPercent(rangeSum(SHOW_DETAILS_LAYOUT.rightColumns, 6, 8) / SHOW_DETAILS_LAYOUT.rightTotal),
    "&__bg": {
      background: theme.palette.surface.main,
      position: "absolute",
      top: "-20px",
      left: 0,
      right: 0,
      bottom: "-20px",
    },
  },

  ".scha__active-quote__btn-col-spacer": {
    width: toPercent(NORMAL_LAYOUT.rightColumns[NORMAL_LAYOUT.rightColumns.length - 1]),
  },
  ".scha__active-quote__total-target-price": {
    background: theme.palette.surface.main,
    width: toPercent(rangeSum(SHOW_DETAILS_LAYOUT.rightColumns, 6, 8)),
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    "&__bg": {
      background: theme.palette.surface.main,
      position: "absolute",
      top: "-30px",
      left: 0,
      right: 0,
      bottom: 0,
    },
  },
  ".scha__active-quote__internal-cell": {
    position: "relative",
    "& > *": {
      position: "relative",
    },
    "&__bg": {
      background: theme.palette.surface.main,
      position: "absolute",
      top: "-50px",
      left: 0,
      right: 0,
      bottom: "-95px",
      zIndex: -1,
    },
  },
  ".scha__active-quote__summary-internal": {
    position: "relative",
    "&__bg": {
      background: theme.palette.surface.main,
      position: "absolute",
      top: "-50px",
      left: 0,
      right: 0,
      bottom: "-95px",
      zIndex: -1,
    },
  },
  ".scha__active-quote__report-buttons": {
    alignItems: "flex-end",
  },
});
